import React, { useState } from 'react';
import { inject, observer } from 'mobx-react';

import SearchResult from './SearchResult';
import RootStore from '../../store/RootStore';
import { IComponentFormOcr } from '../../component';
import SearchRow, { DEFAULT_IMAGE_SEARCH_RESIZE_WIDTH } from './SearchRow';

interface IAllForOcr extends IComponentFormOcr {
  rootStore?: RootStore;
}

type SearchMode = 'text' | 'image';
const SearchModeMap = {
  文本: 'text',
  图片: 'image',
};

const IMAGE_SEARCH_RESIZE_WIDTH = DEFAULT_IMAGE_SEARCH_RESIZE_WIDTH;

const AllForOcr = function (props: IAllForOcr) {
  const { rootStore, placeholder, vectorSearchText, vectorThreshold } = props;
  const { wxuserStore } = rootStore!;
  const [showResult, setShowResult] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchMode, setSearchMode] = useState<SearchMode>(
    SearchModeMap.文本 as SearchMode
  );
  const [imagePreview, setImagePreview] = useState('');

  const enableVectorSearch =
    Number((window.configDetail as any)?.bool_vector || 0) > 0;
  const safeVectorThreshold = (() => {
    const result = Number(vectorThreshold);
    return Number.isNaN(result) ? 0 : result;
  })();

  const resolveGalleryId = () => {
    const dataDetail: any = window.dataDetail || {};
    const gallery: any = window.gallery || {};
    return (
      dataDetail.id ||
      dataDetail.galleryid ||
      dataDetail.gallery_id ||
      gallery.id ||
      gallery.galleryid ||
      gallery.gallery_id
    );
  };

  const resolveOrderId = () => {
    const dataDetail: any = window.dataDetail || {};
    const gallery: any = window.gallery || {};
    const pageDetail: any = window.pageDetail || {};
    const configDetail: any = window.configDetail || {};
    return (
      dataDetail.orderid ||
      dataDetail.order_id ||
      dataDetail.orderDetail?.id ||
      dataDetail.order_detail?.id ||
      gallery.orderid ||
      gallery.order_id ||
      gallery.orderDetail?.id ||
      gallery.order_detail?.id ||
      pageDetail.orderid ||
      pageDetail.order_id ||
      configDetail.orderid ||
      configDetail.order_id
    );
  };

  const searchTextByLegacyOcr = async (number: string) => {
    const galleryId = Number(resolveGalleryId());
    if (!galleryId) {
      setList([]);
      return;
    }
    const result = await wxuserStore.ocr(number, galleryId);
    setList(formatList(result) as never[]);
  };

  const searchNumber = async (number: string) => {
    setShowResult(true);
    setSearchMode(SearchModeMap.文本 as SearchMode);
    setImagePreview('');
    if (keyword !== number) {
      setList([]);
    }
    setKeyword(number);
    setLoading(true);

    try {
      if (enableVectorSearch) {
        const orderId = resolveOrderId();
        const galleryId = resolveGalleryId();
        if (!orderId) {
          console.warn(
            '[AllForOcr] orderId missing in vector text search, fallback to OCR',
            {
              galleryId,
              dataDetail: window.dataDetail,
              gallery: window.gallery,
            }
          );
          await searchTextByLegacyOcr(number);
          return;
        }
        const result = await wxuserStore.aiSearch({
          orderId,
          galleryId,
          queryText: number,
          threshould: safeVectorThreshold,
        });
        setList(formatList(result) as never[]);
      } else {
        await searchTextByLegacyOcr(number);
      }
    } catch (error) {
      console.error('[searchNumber]', error);
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  const searchImage = async (base64: string) => {
    setShowResult(true);
    setSearchMode(SearchModeMap.图片 as SearchMode);
    setKeyword('');
    setImagePreview(base64);
    setList([]);
    setLoading(true);

    try {
      const orderId = resolveOrderId();
      const galleryId = resolveGalleryId();
      if (!orderId) {
        console.warn('[AllForOcr] orderId missing in vector image search', {
          galleryId,
          dataDetail: window.dataDetail,
          gallery: window.gallery,
        });
        setList([]);
        return;
      }
      const result = await wxuserStore.aiSearch({
        orderId,
        galleryId,
        imageBase64: base64,
        threshould: safeVectorThreshold,
      });
      setList(formatList(result) as never[]);
    } catch (error) {
      console.error('[searchImage]', error);
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  const clearSearch = () => {
    setShowResult(false);
    setKeyword('');
    setImagePreview('');
    setList([]);
    setLoading(false);
  };

  const formatList = (list: any[]) => {
    if (!list) {
      return [];
    }
    //处理成需要的数据，注意，这会覆盖掉一部分数据，考虑到覆盖的数据暂时无用，也就无所谓了，实在需要再考虑在这里单独处理
    return list
      .map((one: any) => {
        if (one && Number(one.trash) > 0) {
          return null;
        }
        return one;
      })
      .filter(Boolean);
  };
  return (
    <div className="all-for-ocr">
      <SearchRow
        search={searchNumber}
        searchByImage={searchImage}
        clear={clearSearch}
        placeholder={placeholder}
        enableImageSearch={enableVectorSearch}
        imageSearchText={vectorSearchText}
        imageResizeWidth={IMAGE_SEARCH_RESIZE_WIDTH}
      />
      {showResult && (
        <SearchResult
          mode={searchMode}
          keyword={keyword}
          imagePreview={imagePreview}
          list={list}
          loading={loading}
          onReselectImage={searchImage}
          onSearchKeyword={searchNumber}
          placeholder={placeholder}
          enableImageSearch={enableVectorSearch}
          imageSearchText={vectorSearchText}
          imageResizeWidth={IMAGE_SEARCH_RESIZE_WIDTH}
          close={clearSearch}
        />
      )}
    </div>
  );
};

export default inject('rootStore')(observer(AllForOcr));
